import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Cloud, CloudRain, CloudSun, Sun, Wind, Droplets, Eye,
  Thermometer, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const AdvancedWeather = ({ weather, latitude, longitude }) => {
  const { t } = useI18n();
  const current = weather?.current || {};
  const hourly = weather?.hourly || {};
  const daily = weather?.daily || {};

  // Get next 12 hours forecast
  const getNext12Hours = () => {
    if (!hourly.time) return [];
    const now = new Date();
    const currentHour = now.getHours();
    
    return hourly.time.slice(currentHour, currentHour + 12).map((time, i) => ({
      time: new Date(time).getHours(),
      temp: hourly.temperature_2m?.[currentHour + i],
      clouds: hourly.cloud_cover?.[currentHour + i],
      cloudsLow: hourly.cloud_cover_low?.[currentHour + i],
      cloudsMid: hourly.cloud_cover_mid?.[currentHour + i],
      cloudsHigh: hourly.cloud_cover_high?.[currentHour + i],
      wind: hourly.wind_speed_10m?.[currentHour + i],
      visibility: hourly.visibility?.[currentHour + i]
    }));
  };

  const forecast = getNext12Hours();

  // Calculate "Dramatic Sunset" prediction
  const calculateDramaticScore = () => {
    if (!hourly.cloud_cover_high || !hourly.cloud_cover_low) return null;
    
    const sunsetHourApprox = 16; // Approximate
    const highClouds = hourly.cloud_cover_high?.[sunsetHourApprox] || 0;
    const midClouds = hourly.cloud_cover_mid?.[sunsetHourApprox] || 0;
    const lowClouds = hourly.cloud_cover_low?.[sunsetHourApprox] || 0;
    
    // High clouds = colorful, Low clouds = block sun
    let score = 50;
    
    // High clouds are GOOD (colors)
    if (highClouds > 30 && highClouds < 70) score += 25;
    else if (highClouds > 70) score += 15;
    
    // Mid clouds moderate
    if (midClouds > 20 && midClouds < 50) score += 15;
    
    // Low clouds are BAD (block light)
    if (lowClouds > 60) score -= 30;
    else if (lowClouds > 30) score -= 10;
    
    // Clear sky is just ok
    if (highClouds < 10 && midClouds < 10 && lowClouds < 10) score = 40;
    
    return Math.max(0, Math.min(100, score));
  };

  const dramaticScore = calculateDramaticScore();

  const getCloudIcon = (coverage) => {
    if (coverage < 20) return <Sun className="w-4 h-4 text-amber-500" />;
    if (coverage < 50) return <CloudSun className="w-4 h-4 text-amber-400" />;
    if (coverage < 80) return <Cloud className="w-4 h-4 text-slate-400" />;
    return <CloudRain className="w-4 h-4 text-slate-500" />;
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-500';
    if (score >= 50) return 'text-amber-500';
    return 'text-slate-400';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-500/20 border-emerald-500/30';
    if (score >= 50) return 'bg-amber-500/20 border-amber-500/30';
    return 'bg-slate-500/20 border-slate-500/30';
  };

  return (
    <div className="space-y-4">
      {/* Current Conditions */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Cloud className="w-5 h-5 text-sky-400" />
            {t('weather.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Dramatic Sunset Prediction */}
          {dramaticScore !== null && (
            <div className={`rounded-lg p-4 border ${getScoreBg(dramaticScore)} mb-4`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 mb-1">{t('weather.dramaticSunset')}</p>
                  <p className={`text-3xl font-mono font-bold ${getScoreColor(dramaticScore)}`}>
                    {dramaticScore}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">{t('weather.prediction')}</p>
                  <p className={`text-sm font-medium ${getScoreColor(dramaticScore)}`}>
                    {dramaticScore >= 70 ? `🔥 ${t('weather.dramaticColors')}` :
                     dramaticScore >= 50 ? `✨ ${t('weather.goodPotential')}` :
                     `☁️ ${t('weather.likelyPlain')}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cloud Layers */}
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2">{t('weather.cloudLayers')}</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">{t('weather.high')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${hourly.cloud_cover_high?.[new Date().getHours()] || 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-purple-400 w-10">
                    {hourly.cloud_cover_high?.[new Date().getHours()] || 0}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">{t('weather.mid')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${hourly.cloud_cover_mid?.[new Date().getHours()] || 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-blue-400 w-10">
                    {hourly.cloud_cover_mid?.[new Date().getHours()] || 0}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">{t('weather.low')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-slate-500 rounded-full"
                      style={{ width: `${hourly.cloud_cover_low?.[new Date().getHours()] || 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-slate-400 w-10">
                    {hourly.cloud_cover_low?.[new Date().getHours()] || 0}%
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-600 mt-2">
              💡 {t('weather.cloudTip')}
            </p>
          </div>

          {/* Current Stats Grid */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <Thermometer className="w-4 h-4 text-red-400 mx-auto mb-1" />
              <p className="font-mono text-sm text-white">{current.temperature_2m || '--'}°</p>
              <p className="text-xs text-slate-500">{t('weather.temp')}</p>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <Wind className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
              <p className="font-mono text-sm text-white">{current.wind_speed_10m || '--'}</p>
              <p className="text-xs text-slate-500">km/h</p>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <Eye className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
              <p className="font-mono text-sm text-white">{current.visibility ? (current.visibility/1000).toFixed(0) : '--'}</p>
              <p className="text-xs text-slate-500">km</p>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 text-center">
              <Droplets className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="font-mono text-sm text-white">{current.relative_humidity_2m || '--'}%</p>
              <p className="text-xs text-slate-500">{t('weather.humidity')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hourly Forecast */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading text-slate-300">
            {t('weather.hourlyForecast')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1 overflow-x-auto pb-2">
            {forecast.map((hour, i) => (
              <div 
                key={i} 
                className="flex-shrink-0 w-12 p-2 rounded-lg bg-slate-800/50 text-center"
              >
                <p className="text-xs text-slate-500 mb-1">{hour.time}:00</p>
                {getCloudIcon(hour.clouds || 0)}
                <p className="text-xs font-mono text-white mt-1">{hour.clouds || 0}%</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedWeather;
